import React, { useState } from 'react'
import { BottomNav } from './components/BottomNav'
import HomeTab      from './pages/HomeTab'
import SharpCoreTab from './pages/SharpCoreTab'
import UpcomingMatchesTab from './pages/UpcomingMatchesTab'
import SigmaTab     from './pages/SigmaTab'
import MyPlaysTab   from './pages/MyPlaysTab'
import TrackerTab   from './pages/TrackerTab'
import AlertsTab    from './pages/AlertsTab'
import ContactLogTab from './pages/ContactLogTab'
import UnifiedApp from './unified/UnifiedApp'
import NFLApp from './nfl/NFLApp'
import NFLProofPage from './nfl/ProofPage'
import CFBApp from './cfb/CFBApp'
import NBAApp from './nba/NBAApp'
import CFBProofPage from './cfb/ProofPage'
import NHLApp from './nhl/NHLApp'
import NHLProofPage from './nhl/ProofPage'
import NBAProofPage from './nba/ProofPage'
import Landing from './pages/Landing'
import InstallPrompt from './components/InstallPrompt'
import { PrivacyPolicy, TermsOfService, Disclaimer } from './pages/LegalPages'
import { getSubscription, openBillingPortal } from './api/client'

// First-run gate: a viewer must affirm they are 21+ and accept the Terms + Disclaimer
// before the app renders. Choice is stored locally so it only shows once per device.
// Legal pages (below) bypass this so they stay reachable to anyone.
function AgeGate({ children }) {
  const [ok, setOk] = useState(() => {
    try { return localStorage.getItem('brkr_age_ok') === '1' } catch (_) { return false }
  })
  if (ok) return children
  const accept = () => {
    try { localStorage.setItem('brkr_age_ok', '1') } catch (_) {}
    setOk(true)
  }
  const link = { color: '#2fe37a', textDecoration: 'underline' }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'radial-gradient(900px 600px at 50% -10%, #12331f 0%, #06100a 60%, #050a06 100%)', color: '#eaf3ec', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: 0.5 }}>LINE<span style={{ color: '#2fe37a' }}>BRKR</span></div>
        <div style={{ marginTop: 26, fontSize: 22, fontWeight: 800, lineHeight: 1.3 }}>You must be 21 or older to enter.</div>
        <p style={{ marginTop: 16, fontSize: 14.5, lineHeight: 1.6, color: '#9fb6a7' }}>
          LINEBRKR is a research and analytics tool for entertainment only. It is not betting advice, it does not take wagers, and it does not guarantee outcomes. Only risk what you can afford to lose. Problem gambling? Call or text <b style={{ color: '#eaf3ec' }}>1-800-GAMBLER</b>.
        </p>
        <button onClick={accept} style={{ marginTop: 24, width: '100%', background: '#2fe37a', color: '#05130a', border: 'none', borderRadius: 12, padding: '15px', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          I am 21+ and agree to the Terms
        </button>
        <a href="https://www.google.com" style={{ display: 'block', marginTop: 12, fontSize: 13, color: '#7f9788' }}>I am under 21 — leave</a>
        <div style={{ marginTop: 20, fontSize: 12, color: '#7f9788' }}>
          <a href="/terms" style={link}>Terms</a> · <a href="/privacy" style={link}>Privacy</a> · <a href="/disclaimer" style={link}>Disclaimer</a>
        </div>
      </div>
    </div>
  )
}

// Sport picker — the landing when you open LINEBRKR with two live verticals. Two
// tappable cards route into each board (?sport=mlb / ?sport=nfl). Only shown when
// NFL is enabled; a single-sport build skips it and opens MLB directly.
function SportChooser() {
  const cfbEnabled = import.meta.env.VITE_CFB_ENABLED !== 'false'
  const nhlEnabled = import.meta.env.VITE_NHL_ENABLED !== 'false'
  const nbaEnabled = import.meta.env.VITE_NBA_ENABLED !== 'false'
  const card = (href, tag, name, blurb, accent) => (
    <a href={href} style={{ display: 'block', textDecoration: 'none', background: '#0F1520',
      border: '1px solid #22303f', borderRadius: 16, padding: '20px 20px', color: '#eaf3ec',
      transition: 'border-color .15s, transform .05s' }}
      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.99)'}
      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span style={{ fontSize: 30 }}>{tag}</span>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.02em' }}>{name}</div>
            <div style={{ fontSize: 12.5, color: '#8ea3b0', marginTop: 2 }}>{blurb}</div>
          </div>
        </div>
        <span style={{ color: accent, fontWeight: 800, fontSize: 20 }}>→</span>
      </div>
    </a>
  )
  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(900px 600px at 50% -10%, #12331f 0%, #070d14 55%, #05090f 100%)',
      color: '#eaf3ec', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ fontWeight: 900, fontSize: 32, letterSpacing: '-0.04em' }}>LINE<span style={{ color: '#2fe37a' }}>BRKR</span></div>
        </div>
        <div style={{ textAlign: 'center', color: '#8ea3b0', fontSize: 13.5, marginBottom: 22 }}>Choose your board</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {card('?sport=mlb', '⚾', 'MLB', 'Home runs, hits, K’s & totals', '#2fe37a')}
          {card('?sport=nfl', '🏈', 'NFL', 'Player props · hit % + edge · matchups', '#00D97E')}
          {cfbEnabled && card('?sport=cfb', '🎓', 'College Football', 'Live — player props, injuries & edges', '#00D97E')}
          {nhlEnabled && card('?sport=nhl', '🏒', 'NHL', 'New — shots, points, saves · hit % + edge', '#38BDF8')}
          {nbaEnabled && card('?sport=nba', '🏀', 'NBA', 'Live — player props, injuries & edges', '#ff8a3d')}
        </div>
        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 11, color: '#6f8593', lineHeight: 1.5 }}>
          21+ · Research tool, not betting advice · not a sportsbook
        </div>
      </div>
    </div>
  )
}

// Dedicated login — a clean "enter your access code" screen for returning subscribers, so the
// "Log in" link lands somewhere obvious instead of the pricing wall. Stores the code and
// reloads into the board (an invalid code just re-shows the normal lock screen there).
function LoginScreen() {
  const [code, setCode] = useState('')
  const submit = () => {
    const c = code.trim(); if (!c) return
    try { localStorage.setItem('brkr_access', c) } catch (_) {}
    window.location.href = '/?sport=mlb'
  }
  return (
    <div style={{ minHeight: '100vh', background: '#05090f', color: '#eaf3ec',
      fontFamily: 'system-ui, -apple-system, sans-serif', display: 'flex', alignItems: 'flex-start',
      justifyContent: 'center', padding: '16vh 24px 40px' }}>
      <div style={{ width: '100%', maxWidth: 340, textAlign: 'center' }}>
        <div style={{ fontWeight: 900, fontSize: 30, letterSpacing: '-0.04em', marginBottom: 8 }}>
          <span>LINE</span><span style={{ color: '#00D97E' }}>BRKR</span></div>
        <div style={{ fontSize: 13, color: '#8ea3b0', marginBottom: 18 }}>Enter your access code to log in.</div>
        <input value={code} autoFocus onChange={e => setCode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit() }} placeholder="Access code"
          style={{ width: '100%', boxSizing: 'border-box', background: '#0F1520',
            border: '1px solid #2c3d4e', borderRadius: 10, padding: '13px 14px', color: '#eaf3ec',
            fontSize: 16, textAlign: 'center', letterSpacing: '0.1em', marginBottom: 10 }} />
        <button onClick={submit} style={{ width: '100%', background: '#00D97E', border: 'none',
          borderRadius: 10, padding: '13px 0', color: '#04140D', fontSize: 15, fontWeight: 800,
          cursor: 'pointer' }}>Log in</button>
        <a href="?sport=mlb" style={{ display: 'block', marginTop: 16, fontSize: 13, fontWeight: 700,
          color: '#00D97E', textDecoration: 'none' }}>New here? Start a free trial →</a>
      </div>
    </div>
  )
}

// Account / billing screen (?account=1) — a returning subscriber's home for "what am I
// on?" and self-serve cancel. Reads /billing/subscription for plan+status, and the
// "Manage / cancel subscription" button opens Stripe's hosted Customer Portal via
// /billing/portal (update card, view invoices, cancel). Comp / hand-issued codes have no
// Stripe customer → we show that plainly instead of a dead button.
function AccountScreen() {
  const [sub, setSub] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState('')
  let code = ''; try { code = localStorage.getItem('brkr_access') || '' } catch (_) {}

  React.useEffect(() => {
    let live = true
    getSubscription()
      .then(d => { if (live) setSub(d) })
      .catch(() => { if (live) setSub(null) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  const manage = async () => {
    setBusy(true); setErr('')
    try {
      const { url } = await openBillingPortal()
      window.location.href = url
    } catch (e) {
      setErr(e?.message || 'Could not open billing portal.')
      setBusy(false)
    }
  }

  const box = { width: '100%', maxWidth: 380, textAlign: 'center' }
  const wrap = { minHeight: '100vh', background: '#05090f', color: '#eaf3ec',
    fontFamily: 'system-ui, -apple-system, sans-serif', display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center', padding: '12vh 24px 40px' }
  const card = { background: '#0F1520', border: '1px solid #22303f', borderRadius: 14,
    padding: '18px 18px', textAlign: 'left', marginTop: 16 }
  const label = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7f9788' }
  const val = { fontSize: 15, fontWeight: 700, marginTop: 2 }
  const btn = (bg, fg) => ({ width: '100%', background: bg, color: fg, border: 'none',
    borderRadius: 10, padding: '13px 0', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginTop: 14 })
  const nice = { active: 'Active', trialing: 'Free trial', past_due: 'Payment past due',
    canceled: 'Canceled', unpaid: 'Unpaid', incomplete: 'Incomplete' }
  const planNice = { pro: 'Full Access', all: 'Full Access', founder: 'Founder (locked for life)',
    pro_founder: 'Founder (locked for life)', rookie: 'Rookie', free: 'Free' }

  return (
    <div style={wrap}>
      <div style={box}>
        <div style={{ fontWeight: 900, fontSize: 28, letterSpacing: '-0.04em', marginBottom: 6 }}>
          <span>LINE</span><span style={{ color: '#00D97E' }}>BRKR</span></div>
        <div style={{ fontSize: 13, color: '#8ea3b0' }}>Your account</div>

        {loading ? (
          <div style={{ marginTop: 30, color: '#7f9788', fontSize: 14 }}>Loading…</div>
        ) : !code ? (
          <div style={card}>
            <div style={{ fontSize: 14.5, color: '#cfe0d6', lineHeight: 1.5 }}>
              You’re not signed in on this device. Enter your access code to manage your subscription.</div>
            <a href="?login=1" style={{ ...btn('#00D97E', '#04140D'), display: 'block',
              textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>Log in</a>
          </div>
        ) : (
          <>
            <div style={card}>
              <div style={label}>Plan</div>
              <div style={val}>{planNice[sub?.plan] || sub?.plan || 'Full Access'}</div>
              {sub?.status && (<>
                <div style={{ ...label, marginTop: 12 }}>Status</div>
                <div style={{ ...val, color: sub.status === 'past_due' ? '#ff9f43'
                  : sub.status === 'canceled' ? '#ff6b6b' : '#2fe37a' }}>
                  {nice[sub.status] || sub.status}</div>
              </>)}
              {sub?.email && (<>
                <div style={{ ...label, marginTop: 12 }}>Email</div>
                <div style={val}>{sub.email}</div>
              </>)}
              <div style={{ ...label, marginTop: 12 }}>Access code</div>
              <div style={{ ...val, letterSpacing: '0.08em', fontFamily: 'ui-monospace, monospace' }}>{code}</div>
            </div>

            {sub?.can_manage ? (
              <button onClick={manage} disabled={busy} style={btn(busy ? '#173a26' : '#00D97E', '#04140D')}>
                {busy ? 'Opening…' : 'Manage / cancel subscription'}</button>
            ) : (
              <div style={{ ...card, background: '#12181f' }}>
                <div style={{ fontSize: 13.5, color: '#9fb6a7', lineHeight: 1.55 }}>
                  This access code isn’t tied to a billing account (a comp or invite code), so there’s
                  nothing to cancel here. Questions? Email <b style={{ color: '#eaf3ec' }}>support@linebrkr.app</b>.</div>
              </div>
            )}
            {err && <div style={{ marginTop: 12, color: '#ff8a8a', fontSize: 13, lineHeight: 1.5 }}>{err}</div>}
          </>
        )}

        <a href="?sport=mlb" style={{ display: 'block', marginTop: 20, fontSize: 13, fontWeight: 700,
          color: '#8ea3b0', textDecoration: 'none' }}>← Back to the board</a>
      </div>
    </div>
  )
}

// One-time access-code reveal — shown right after a successful checkout claim (both MLB and
// NFL set localStorage 'brkr_new_code' then reload through here). This is what makes the
// system work end-to-end WITHOUT email: the buyer sees and saves the code that logs them in
// on any device. They're already logged in on this device; the code is also always available
// later under ?account=1. Dismissing clears the flag and drops them into their board.
function NewCodeScreen({ code }) {
  const [copied, setCopied] = React.useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1800) } catch (_) {}
  }
  const done = () => {
    try { localStorage.removeItem('brkr_new_code') } catch (_) {}
    window.location.reload()   // keeps ?sport=… → lands back in the board they bought
  }
  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(900px 600px at 50% -10%, #12331f 0%, #06100a 60%, #050a06 100%)',
      color: '#eaf3ec', fontFamily: 'system-ui, -apple-system, sans-serif', display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center', padding: '12vh 24px 40px' }}>
      <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>✅</div>
        <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.02em', marginTop: 8 }}>You’re in.</div>
        <div style={{ fontSize: 14, color: '#9fb6a7', marginTop: 8, lineHeight: 1.5 }}>
          This is your <b style={{ color: '#eaf3ec' }}>access code</b> — it’s how you log in on any other device.
          Save it somewhere safe.</div>
        <div style={{ marginTop: 20, background: '#0F1520', border: '1px solid #2c3d4e', borderRadius: 12,
          padding: '18px 14px' }}>
          <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: '0.14em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>{code}</div>
        </div>
        <button onClick={copy} style={{ width: '100%', marginTop: 12, background: 'transparent',
          border: '1px solid #2c3d4e', borderRadius: 10, padding: '11px 0', color: copied ? '#2fe37a' : '#eaf3ec',
          fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>{copied ? 'Copied ✓' : 'Copy code'}</button>
        <button onClick={done} style={{ width: '100%', marginTop: 10, background: '#00D97E', border: 'none',
          borderRadius: 10, padding: '14px 0', color: '#04140D', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
          Enter LINEBRKR →</button>
        {/* Peak moment to get the app on their home screen — one tap on Android, Share-sheet
            steps on iOS. Hides automatically if they're already running it installed. */}
        <InstallPrompt variant="cta" />
        <div style={{ marginTop: 14, fontSize: 12, color: '#7f9788', lineHeight: 1.5 }}>
          You can always find this again under <b style={{ color: '#9fb6a7' }}>Manage subscription</b>.</div>
      </div>
    </div>
  )
}

export default function App() {
  const [tab, setTab] = useState('home')

  // Standalone legal pages — accessible without logging in (and NOT age-gated). nginx
  // serves index.html for any path (SPA fallback), so these resolve here.
  const path = window.location.pathname.replace(/\/+$/, '')
  if (path === '/privacy')    return <PrivacyPolicy />
  if (path === '/terms')      return <TermsOfService />
  if (path === '/disclaimer') return <Disclaimer />
  // Public NFL track-record page — the shareable proof link. Bypasses the age +
  // access gates (like the legal pages) and reads only settled, graded results.
  if (path === '/proof')      return <NFLProofPage />
  // Public CFB track-record page — same shareable, gate-free treatment as /proof.
  if (path === '/cfb/proof')  return <CFBProofPage />
  // Public NHL track-record page — same shareable, gate-free treatment as /proof.
  if (path === '/nhl/proof')  return <NHLProofPage />
  // Public NBA track-record page — same shareable, gate-free treatment as /proof.
  if (path === '/nba/proof')  return <NBAProofPage />

  const params = new URLSearchParams(window.location.search)
  const sport = params.get('sport')
  const ui = params.get('ui')

  // Referral capture: a visitor arriving via ?ref=<code> has that tag stored so it rides
  // through the free signup + paid checkout for attribution (set once, first-touch wins).
  const refTag = params.get('ref')
  if (refTag) { try { if (!localStorage.getItem('brkr_ref')) localStorage.setItem('brkr_ref', refTag) } catch (_) {} }

  // Dedicated login route (?login=1) — a clean access-code screen for returning subscribers.
  if (params.get('login')) return <LoginScreen />

  // Account / billing route (?account=1) — plan status + self-serve cancel (Stripe portal).
  // Also the Customer Portal's return_url, so a user lands back here after managing billing.
  if (params.get('account')) return <AccountScreen />

  // One-time code reveal after a fresh checkout (flag set by the MLB/NFL claim handlers).
  // Runs before sport routing so it catches both boards. Not age-gated — the buyer already
  // passed the gate on the way to checkout.
  let freshCode = ''
  try { freshCode = localStorage.getItem('brkr_new_code') || '' } catch (_) {}
  if (freshCode) return <NewCodeScreen code={freshCode} />

  // Returning from Stripe checkout with ?session_id but no sport (an MLB purchase) → send
  // straight into the app so it exchanges the session for the access code and logs them in,
  // instead of dropping onto the marketing landing (which never runs the claim). NFL/CFB/NBA
  // checkouts carry &sport=… and route to their own app below.
  if (params.get('session_id') && !sport && ui !== 'legacy') {
    return <AgeGate><UnifiedApp /></AgeGate>
  }

  // NFL (isolated, additive). Kill-switch: set VITE_NFL_ENABLED="false" to close the
  // door instantly (also hides the sport picker → opens MLB directly). See src/nfl/.
  const nflEnabled = import.meta.env.VITE_NFL_ENABLED !== 'false'
  if (nflEnabled && sport === 'nfl') {
    return <AgeGate><NFLApp /></AgeGate>
  }

  // CFB (isolated, additive). Kill-switch: set VITE_CFB_ENABLED="false" to hide the
  // door instantly (also hides the sport picker card). See src/cfb/.
  const cfbEnabled = import.meta.env.VITE_CFB_ENABLED !== 'false'
  if (cfbEnabled && sport === 'cfb') {
    return <AgeGate><CFBApp /></AgeGate>
  }

  // NHL (isolated, additive). Kill-switch: set VITE_NHL_ENABLED="false" to hide the
  // door instantly (also hides the sport picker card). See src/nhl/.
  const nhlEnabled = import.meta.env.VITE_NHL_ENABLED !== 'false'
  if (nhlEnabled && sport === 'nhl') {
    return <AgeGate><NHLApp /></AgeGate>
  }

  // NBA (isolated, additive). Kill-switch: set VITE_NBA_ENABLED="false" to hide the
  // door instantly (also hides the sport picker card). See src/nba/.
  const nbaEnabled = import.meta.env.VITE_NBA_ENABLED !== 'false'
  if (nbaEnabled && sport === 'nba') {
    return <AgeGate><NBAApp /></AgeGate>
  }

  // Front door for a visit with no sport chosen yet. EXISTING users (anyone with a
  // stored access code) skip the marketing page and land on their normal board picker,
  // exactly as before — so nothing changes for people who already log in. FRESH visitors
  // get the marketing landing (public, not age-gated, like the legal pages; the 21+ gate
  // fires when a CTA enters a board). ?ui=legacy skips straight into the old app.
  if (!sport && ui !== 'legacy') {
    let hasCode = false
    try { hasCode = !!localStorage.getItem('brkr_access') } catch (_) {}
    if (hasCode) return <AgeGate><><SportChooser /><InstallPrompt variant="banner" /></></AgeGate>
    return <Landing />
  }

  // V2 "unified" UI is now the default experience. The legacy bottom-nav app
  // is kept as a one-tap fallback: append ?ui=legacy to load it.
  if (ui !== 'legacy') {
    return <AgeGate><><UnifiedApp /><InstallPrompt variant="banner" /></></AgeGate>
  }

  return (
    <AgeGate>
    <div style={{ background: '#06080F', minHeight: '100vh', maxWidth: 430, margin: '0 auto', position: 'relative' }}>
      {tab === 'home'    && <HomeTab />}
      {tab === 'board'   && <SharpCoreTab />}
      {tab === 'matches' && <UpcomingMatchesTab />}
      {tab === 'sigma'   && <SigmaTab />}
      {tab === 'plays'   && <MyPlaysTab />}
      {tab === 'tracker' && <TrackerTab />}
      {tab === 'contact' && <ContactLogTab />}
      {tab === 'alerts'  && <AlertsTab />}
      <BottomNav active={tab} onSelect={setTab} />
    </div>
    </AgeGate>
  )
}
